import * as Sentry from "@sentry/react";
import { ENV } from "~/config/env.config";
import { allowDemoEvent } from "~/config/sentry.client.config";
import { useAuthStore } from "~/store/authentication/useAuthStore";
import type { UserProfile } from "~/types/auth";

// Only skip Sentry in development mode
// Staging and production should both send to Sentry (with proper environment tags)
const isDevelopment = ENV.ENVIRONMENT === "development";

/**
 * Check if a status code should be logged to Sentry
 * Logs: 5xx (server errors), 404 (not found), 429 (rate limited)
 */
export function shouldLogStatus(status: number): boolean {
  return status >= 500 || status === 404 || status === 429;
}

/**
 * Log levels for different types of events
 */
export type LogLevel = "error" | "warning" | "info" | "debug";

function getPrimaryUserOrganization(profile: UserProfile | null) {
  return (
    profile?.userOrganizations?.find((userOrg) => userOrg.isPrimary) ??
    profile?.userOrganizations?.[0]
  );
}

function getSentryContextTags(profile: UserProfile | null) {
  const { selectedLocationId, role: storedRole } = useAuthStore.getState();
  const primaryUserOrg = getPrimaryUserOrganization(profile);

  const organizationId =
    profile?.organisation?.id ??
    primaryUserOrg?.organizationId ??
    primaryUserOrg?.organization?.id;

  const primaryLocation =
    primaryUserOrg?.organization?.locations?.[0] ??
    profile?.organisation?.locations?.[0];

  const locationId =
    selectedLocationId ??
    profile?.employee?.locationId ??
    profile?.userLocations?.[0]?.locationId ??
    primaryLocation?.id;

  const matchedLocation = profile?.userLocations?.find(
    (location) => location.locationId === locationId,
  );

  // XD-055 — profile is persisted to localStorage, so a page load can still
  // be serving a pre-migration cached blob that never carried permissionSet.
  // Twin first, legacy `.role` fallback tagged for the eventual alias removal.
  const role =
    matchedLocation?.permissionSet?.name ??
    matchedLocation?.role ?? // XD-055 alias fallback
    profile?.userLocations?.[0]?.permissionSet?.name ??
    profile?.userLocations?.[0]?.role ?? // XD-055 alias fallback
    primaryUserOrg?.permissionSet?.name ??
    primaryUserOrg?.role?.name ?? // XD-055 alias fallback
    storedRole;

  return {
    organization_id: organizationId,
    location_id: locationId,
    restaurant_id: locationId,
    role,
  };
}

function applyContextTags(
  setTag: (key: string, value: string) => void,
  profile: UserProfile | null,
) {
  Object.entries(getSentryContextTags(profile)).forEach(([key, value]) => {
    if (value) {
      setTag(key, value);
    }
  });
}

/**
 * Keep Sentry's global user/tenant context aligned with the active session.
 */
export function setSentryUserContext(profile: UserProfile | null) {
  if (!profile) {
    Sentry.setUser(null);
    Sentry.setTag("organization_id", "");
    Sentry.setTag("location_id", "");
    Sentry.setTag("restaurant_id", "");
    Sentry.setTag("role", "");
    return;
  }

  // Opaque id only — never email/phone (admin#936). Quality Triage reads only
  // id/title/exception/tags.service/server_name, so nothing needs more.
  Sentry.setUser({ id: profile.id });

  applyContextTags(Sentry.setTag, profile);
}

/**
 * Keys whose VALUE is a person's contact detail, matched as a case- and
 * punctuation-insensitive SUFFIX (the same way the beforeSend scrubber matches
 * secrets): guestEmail / recipientEmail / guestPhone / emergencyContactPhone /
 * twilioPhoneNumber match; emailEnabled / emailOptIn / phonePrefix do not.
 */
const CONTACT_PII_KEY_SUFFIXES = ["email", "phone", "phonenumber"];
const CONTACT_PII_MAX_DEPTH = 10;
const REDACTED = "[Redacted]";

function isContactPiiKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  return CONTACT_PII_KEY_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Copy of `value` with every contact-PII key's value redacted (admin#936).
 *
 * `handleApiError` attaches each failed request's payload to the Sentry event
 * (`extraData: { payload: data }` at ~100 call sites — guest, employee,
 * contact and reservation create/update among them). setUser is opaque-id
 * only, but without this a guest's or employee's email/phone still reached
 * Sentry on any API error: the beforeSend scrubber redacts SECRETS (tokens,
 * PINs, card data), not contact details. The rule lives here, where admin
 * attaches payloads, rather than in the scrubber that every frontend shares.
 *
 * Walks plain objects and arrays only (a Date/File/FormData is passed
 * through), bounded in depth, and never returns an unredacted cycle.
 */
export function redactContactPii(
  value: unknown,
  depth = 0,
  path: Set<object> = new Set(),
): unknown {
  if (value === null || typeof value !== "object") return value;
  if (!Array.isArray(value) && !isPlainObject(value)) return value;
  if (path.has(value)) return "[Circular]";
  if (depth >= CONTACT_PII_MAX_DEPTH) return "[Truncated]";
  path.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item: unknown) => redactContactPii(item, depth + 1, path));
    }
    const copy: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      copy[key] =
        isContactPiiKey(key) && child != null && child !== ""
          ? REDACTED
          : redactContactPii(child, depth + 1, path);
    }
    return copy;
  } finally {
    path.delete(value);
  }
}

/**
 * Log an error to Sentry (production) or console (development).
 *
 * Pass `forceSend: true` to bypass the development guard and deliver the
 * event to Sentry anyway (used by the observability demo page). Events sent
 * this way are automatically tagged with `demo.observability` so the
 * `beforeSend` hook in sentry.client.config.ts lets them through.
 */
export function logEvent(
  message: string,
  options?: {
    category?: string;
    level?: LogLevel;
    data?: Record<string, unknown>;
    error?: unknown;
    tags?: Record<string, string>;
    forceSend?: boolean;
  }
) {
  const {
    category = "general",
    level = "error",
    data,
    error,
    tags,
    forceSend = false,
  } = options || {};

  if (level === "info") {
    if (isDevelopment) {
      const logData: Record<string, unknown> = {
        category,
        level,
      };

      if (data) {
        logData.data = data;
      }

      if (error) {
        logData.error =
          error instanceof Error
            ? {
                message: error.message,
                stack: error.stack,
              }
            : error;
      }

      if (tags) {
        logData.tags = tags;
      }

      const formattedData = JSON.stringify(logData, null, 2);
      console.info(`[${category.toUpperCase()}] ${message}`, formattedData);
    }
    return;
  }

  if (isDevelopment && !forceSend) {
    const consoleMethod =
      level === "error"
        ? console.error
        : level === "warning"
          ? console.warn
          : console.log;

    const logData: Record<string, unknown> = {
      category,
      level,
    };

    if (data) {
      logData.data = data;
    }

    if (error) {
      logData.error =
        error instanceof Error
          ? {
              message: error.message,
              stack: error.stack,
            }
          : error;
    }

    if (tags) {
      logData.tags = tags;
    }

    const formattedData = JSON.stringify(logData, null, 2);
    consoleMethod(`[${category.toUpperCase()}] ${message}`, formattedData);
    return;
  }

  const allTags: Record<string, string> = { category };

  const contextTags = getSentryContextTags(useAuthStore.getState().profile);
  for (const [key, value] of Object.entries(contextTags)) {
    if (value) allTags[key] = value;
  }

  if (forceSend) {
    allTags["demo.observability"] = "true";
  }

  if (tags) {
    Object.assign(allTags, tags);
  }

  const contexts: Record<string, Record<string, unknown>> = {};
  if (data) {
    contexts.error_details = redactContactPii(data) as Record<string, unknown>;
  }
  if (error) {
    contexts.error = {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    };
  }

  if (forceSend) {
    allowDemoEvent();
  }

  if (error) {
    Sentry.captureException(error as Error, { tags: allTags, contexts, level });
  } else {
    Sentry.captureMessage(message, { tags: allTags, contexts, level });
  }
}

/**
 * Log an error (shorthand for logEvent with level: "error")
 */
export function logError(
  message: string,
  error?: unknown,
  options?: {
    category?: string;
    data?: Record<string, unknown>;
    tags?: Record<string, string>;
    forceSend?: boolean;
  }
) {
  logEvent(message, {
    ...options,
    level: "error",
    error,
  });
}

/**
 * Log a warning (shorthand for logEvent with level: "warning")
 */
export function logWarning(
  message: string,
  options?: {
    category?: string;
    data?: Record<string, unknown>;
    tags?: Record<string, string>;
    forceSend?: boolean;
  }
) {
  logEvent(message, {
    ...options,
    level: "warning",
  });
}

/**
 * Local debug / trace logging (development console only). Never sent to Sentry.
 * Use `sLog` from ~/lib/sentry-log for structured business logs in staging/prod;
 * use logError / logWarning for issue-level events.
 */
export function logInfo(
  message: string,
  options?: {
    category?: string;
    data?: Record<string, unknown>;
    tags?: Record<string, string>;
    forceSend?: boolean;
  }
) {
  logEvent(message, {
    ...options,
    level: "info",
  });
}

/**
 * Add a breadcrumb to the current Sentry scope.
 * Breadcrumbs are attached to the next captured event for debugging context.
 */
export function addBreadcrumb(crumb: {
  category?: string;
  message?: string;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
  data?: Record<string, unknown>;
}) {
  Sentry.addBreadcrumb(crumb);
}
